import { sum } from "lodash";
import { CachedItem } from "@lib-shared/lib";
import { Key } from "core/constants";
import { logApiError } from "core/lib";
import { User } from "core/models";
import { SmartCard } from "core/models/primary-smart-card";
import { CustomerAccountService } from "core/services/cef";
import { CustomerProduct, PackageInfo, Service } from "core/services/cef/customer-account.service";
import { FinancialAccount } from "core/services/cef/customer-account.service.types";
import SubscriptionStore, { Bouquet, AgreementSubscription, SubscriptionParent } from "./subscriptions.store";
/*
Test Data:
{subscription({ packageType: "Play", totalTVs: 5, status: "active" }, 1)}
{subscription({ packageType: "Work", totalTVs: 5, status: "inactive" }, 2)}
{subscription({ packageType: "Stay", totalTVs: 5, status: "pending" }, 3)}
*/

class SubscriptionStoreManager {
    store: SubscriptionStore;
    private cache = new CachedItem(Key.products, true, 3600);
    private agreementsCache = new CachedItem(Key.agreements, true, 3600);
    private byAgreementCache = new CachedItem("byAgreement", true, 3600);
    private primaryServiceCache = new CachedItem(Key.primaryService, true, 7200);

    constructor(store: SubscriptionStore) {
        this.store = store;
    }

    reset() {
        console.log("Resetting");
        this.store.reset();
        this.cache.clear();
        this.primaryServiceCache.clear();
    }

    //TODO: Refactor
    private populateAgreements = (data: Array<FinancialAccount>) => {
        const principalType = "Principal";
        const bouquets = new Array<Bouquet>();
        let allSmartcardNumbers: Array<string> = [];
        let primarySmartCardNumber: SmartCard | undefined = undefined;

        for (let i = 0; i < data.length; i++) {
            const financialAccount = data[i];

            if (!financialAccount?.agreements?.length) {
                continue;
            }

            for (let a = 0; a < financialAccount.agreements.length; a++) {
                const agreement = financialAccount.agreements[a];

                const smartcardNumbers = agreement.listOfHardware?.map(x => x.smartcardNumber) ?? [];

                if (smartcardNumbers.length) {
                    allSmartcardNumbers = [...allSmartcardNumbers, ...smartcardNumbers];
                }

                const agreementPackage = agreement?.products?.find(x => x.type === principalType);

                if (!agreementPackage) {
                    continue;
                }

                let bouquet = bouquets.find(x => x.name === agreementPackage.bouquetName);

                if (!bouquet) {
                    bouquet = {
                        name: agreementPackage.bouquetName ?? "",
                        packages: [],
                        tvPoints: 0
                    };

                    bouquets.push(bouquet);
                }

                let bouquetPackage = bouquet.packages.find((x: { name: string; }) => x.name === agreementPackage.name);

                if (!bouquetPackage) {
                    bouquetPackage = {
                        name: agreementPackage.name,
                        tvPoints: 0
                    };
                    bouquet.packages.push(bouquetPackage);
                }

                const tvPoints = parseInt(agreement.agreementTvPoints.toString());
                bouquetPackage.tvPoints += tvPoints;
                bouquet.tvPoints += tvPoints;

                if (!primarySmartCardNumber && smartcardNumbers.length) {
                    primarySmartCardNumber = {
                        smartCardNumber: smartcardNumbers[0],
                        productName: agreementPackage.name
                    };
                }
            }
        }

        // bouquets.forEach(x => {
        //     x.tvPoints = x.packages.map(x => x.tvPoints).reduce((a, b) => a + b, 0);
        // });

        if (!bouquets.length) {
            console.info("There are no bouquets");
        }

        this.store.updateAgreements(bouquets);
        this.store.updatePrimarySmartCard(primarySmartCardNumber);
        this.store.updateSmartcardNumbers(allSmartcardNumbers);
    };

    //TODO: Refactor
    private populatePackagesByAgreement = (data: CustomerProduct[]) => {
        const list: AgreementSubscription[] = [];

        const packs = this.store.packages;

        if (!packs) {
            return;
        }

        const agreements = data.map(x => x.agreements).flatMap((x) => x).filter(x => x !== undefined && x.hardwares && x.hardwares.length > 0);

        for (let a = 0; a < agreements.length; a++) {
            const agreement = agreements[a];

            const services: Service[] = [];

            const subscriptionsAndSmartcardNumbers: Array<any> = [];

            const hardwaresWithServices = agreement.hardwares.filter(x => x.services && x.services.length > 0);

            for (let h = 0; h < hardwaresWithServices.length; h++) {
                const hardware = hardwaresWithServices[h];
                const smartcardNumber = hardware.smartcardNumber;

                for (let s = 0; s < hardware.services.length; s++) {
                    const service = hardware.services[s];

                    if (!services.find(x => x.packageDetail.packageName === service.packageDetail.packageName)) {

                        services.push(service);
                    }

                    const existing = subscriptionsAndSmartcardNumbers.find(x => x.name === service.packageDetail.packageName);

                    if (existing) {
                        existing.smartcardNumbers.push(smartcardNumber);
                        continue;
                    }

                    subscriptionsAndSmartcardNumbers.push({
                        name: service.packageDetail.packageName,
                        smartcardNumbers: [smartcardNumber]
                    });
                }
            }

            if (services.length === 0) {
                continue;
            }

            for (let s = 0; s < services.length; s++) {
                const packageName = services[s].packageDetail.packageName as any;
                const service = services[s] as any;

                service.smartcardNumbers = subscriptionsAndSmartcardNumbers.find(x => x.name === packageName)?.smartcardNumbers;
            }

            const summary: AgreementSubscription = {
                id: parseInt(agreement.agreementId),
                name: agreement.establishmentName,
                tvPoints: parseInt(agreement.numberOfViewingPoints),
                location: agreement.locationIndicator,
                status: agreement.agreementStatus,
                subscriptions: []
            };

            for (let p = 0; p < packs.length; p++) {
                const pack = packs[p];

                let hasSubscription = false;

                for (let pi = 0; pi < pack.data.length; pi++) {
                    hasSubscription = services.find(x => x.packageDetail.packageName) !== undefined;

                    if (hasSubscription) {
                        break;
                    }
                }

                if (!hasSubscription) {
                    continue;
                }

                const parent: SubscriptionParent = {
                    packageType: pack.packageType,
                    status: pack.status,
                    packages: []
                };

                const dataNames = pack.data.map(x => x.packageType);

                parent.packages = services.filter(x => dataNames.includes(x.packageDetail.packageName));

                if (parent.packages.length > 0) {
                    summary.subscriptions.push(parent);
                }
            }

            const userAgreement = User.instance?.company?.agreements.find((x: { id: number; }) => x.id === summary.id);

            if (userAgreement) {
                summary.name = userAgreement.friendlyName;
            }

            list.push(summary);
        }

        this.byAgreementCache.set(list);

        this.store.updateAgreementPackages(list);

        return list;
    };

    private populatePackages = (data: CustomerProduct[]) => {
        console.log(`Populating packages from data: ${data.length}`);

        const packages = data.map(x => x.packageInfo).flatMap((x) => x).filter(x => x !== undefined);

        const distinctPackages: PackageInfo[] = [];

        packages.forEach(pi => {
            const existing: PackageInfo | undefined = distinctPackages.find(x => x?.packageType === pi?.packageType);

            if (!existing) {
                distinctPackages.push(pi);
                return;
            }

            pi.data.forEach(p => {
                const existingPackageInfo = existing.data.find(y => y.packageType === p.packageType);

                if (!existingPackageInfo) {
                    existing.data.push(p);
                    return;
                }

                existingPackageInfo.totalTV = (parseInt(existingPackageInfo?.totalTV ?? "0") + parseInt(p.totalTV)).toString();
            });
        });

        distinctPackages.forEach(pi => {
            if (!pi) {
                return;
            }

            const totalTvs = sum(pi.data.map(p => parseInt(p.totalTV)));
            pi.totalTV = totalTvs.toString();
        });

        this.store.updatePackages(distinctPackages);
    };

    populateAsync = async () => {
        if (this.store.isLoading
            || ((this.store.bouquets?.length ?? 0) > 0)) {
            return;
        }

        this.store.updateIsLoading(true);

        // if (this.cache.isCached && this.primaryServiceCache.isCached && this.agreementsCache.isCached) {
        //     console.log("Using cached data");
        //     const data = this.cache.value;
        //     this.populatePackages(data);
        //     this.populatePackagesByAgreement(data);
        //     const bouquetData = this.agreementsCache.value;
        //     this.populateAgreements(bouquetData);
        //     return;
        // }
        // try {
        //     const response = await new CustomerAccountService().getSubscriptions();
        //     const data = response?.data;
        //     this.populatePackages(data);
        //     this.populatePackagesByAgreement(data);
        //     this.cache.set(data);
        //     this.store.updateHasError(false);
        // } catch (error) {
        //     console.error("User Subscriptions error: ", error);
        //     this.store.updateHasError(true);
        // }
        try {
            let data: FinancialAccount[];

            if (this.agreementsCache.isCached) {
                data = this.agreementsCache.value;
            } else {
                const response = await new CustomerAccountService().getAgreements();
                data = response?.data;
                this.agreementsCache.set(data);
            }

            this.populateAgreements(data);
        } catch (error) {
            logApiError(error);

            this.store.updateHasError(true);
        }

        this.store.updateIsLoading(false);
    };
}

export default SubscriptionStoreManager;
